import { FlexBox, LoadingButton } from '@airship-platform/jam-web/components';
import {
  EmailValidationSchema,
  PasswordValidationSchema,
} from '@airship-platform/common/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PropsWithChildren } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const signInSchema = z.object({
  email: EmailValidationSchema.min(1, 'Email is required'),
  password: PasswordValidationSchema,
});

type SignInFormInputs = z.infer<typeof signInSchema>;

type SignInFormProps = {
  isLoading: boolean;
  errorMessage?: string;
  signIn: (email: string, password: string) => Promise<void>;
};

const SignInForm: React.FC<PropsWithChildren<SignInFormProps>> = ({
  signIn,
  isLoading,
  errorMessage,
  children,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormInputs>({
    defaultValues: { email: '', password: '' },
    resolver: zodResolver(signInSchema),
  });

  const handleSignIn: SubmitHandler<SignInFormInputs> = (data) =>
    signIn(data.email, data.password);

  return (
    <FlexBox flexDirection="column" gap={3} width={'100%'}>
      <Typography variant="h2" color="secondary">
        Sign In
      </Typography>
      {errorMessage && <Typography color="error">{errorMessage}</Typography>}
      <Box
        component={'form'}
        onSubmit={handleSubmit(handleSignIn)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
        }}
      >
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Email"
              variant="outlined"
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message}
              autoComplete="username"
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Password"
              type="password"
              variant="outlined"
              fullWidth
              error={!!errors.password}
              helperText={errors.password?.message}
              autoComplete="current-password"
            />
          )}
        />
        {children}
        <LoadingButton
          loading={isLoading}
          disabled={isLoading}
          type="submit"
          size="large"
          fullWidth
        >
          Sign In
        </LoadingButton>
        <Typography variant="body2">
          Don't have an account?{' '}
          <Link component="a" href="/signup" variant="body2">
            Sign up
          </Link>
        </Typography>
      </Box>
    </FlexBox>
  );
};

export default SignInForm;
